import { Link } from "react-router-dom";
import type { Account, Backtest, OrderRecord } from "@/api/types";
import { Dialog } from "@/components/ui/Dialog";
import { ExchangeMark, Pill, type Tone } from "@/components/ui/kit";
import { useFormatDate } from "@/hooks/useFormatDate";
import { isLiveInstance } from "@/lib/live";
import { humanError, orderAction, orderReason, pendingAge } from "@/lib/order-labels";
import { fillPrice } from "@/lib/order-trades";
import { num, pnlColor, qtyOf, signedUsd, usd, valueOf } from "@/lib/order-format";

const STATUS_PILL: Record<string, { tone: Tone; label: string }> = {
  done: { tone: "ok", label: "Filled" },
  pending: { tone: "info", label: "Pending" },
  canceled: { tone: "mute", label: "Canceled" },
  error: { tone: "bad", label: "Failed" },
  skipped: { tone: "warn", label: "Skipped" },
};

const detailRow = "flex items-baseline justify-between gap-4 border-t border-[var(--divider)] py-2 text-[13px] first:border-t-0";

/**
 * Everything about one order that the simple tables leave out: price, value,
 * status and why, the exchange's own ID. `pnl` is its realized result:
 * undefined when it opened a position, null when a price is missing.
 */
export function OrderDetailsDialog({
  order: o,
  account,
  strategy,
  pnl,
  onClose,
}: {
  order: OrderRecord;
  account?: Account;
  strategy?: Backtest;
  pnl: number | null | undefined;
  onClose: () => void;
}) {
  const { dateTime } = useFormatDate();
  const stale = pendingAge(o, Date.now());
  const pill = stale ? { tone: "warn" as Tone, label: `Not confirmed · ${stale}` } : STATUS_PILL[o.status] || { tone: "mute" as Tone, label: o.status };
  const failed = o.status === "error" || o.status === "skipped";
  const why = failed && o.error ? humanError(o.error) : orderReason(o.comment);
  const price = fillPrice(o);
  const requested = Number(o.requested_price);
  return (
    <Dialog
      title={
        <span style={{ color: o.side === "BUY" ? "var(--success)" : "var(--danger)" }}>
          {orderAction(o).label} <span className="text-[var(--text-primary)]">{o.symbol}</span>
        </span>
      }
      onClose={onClose}
    >
      <div>
        <div className={detailRow}>
          <span className="text-[var(--text-muted)]">Status</span>
          <span className="text-right">
            <Pill tone={pill.tone}>{pill.label}</Pill>
            {why && <span className="ml-2 text-xs text-[var(--text-muted)]">{why}</span>}
          </span>
        </div>
        <div className={detailRow}>
          <span className="text-[var(--text-muted)]">P&amp;L</span>
          <span className="num font-semibold" style={{ color: pnlColor(pnl) }}>
            {pnl === undefined ? "Opened a position" : pnl == null ? "—" : signedUsd(pnl)}
          </span>
        </div>
        <div className={detailRow}>
          <span className="text-[var(--text-muted)]">Time</span>
          <span className="num">{dateTime(o.created_at)}</span>
        </div>
        <div className={detailRow}>
          <span className="text-[var(--text-muted)]">Exchange</span>
          <span className="inline-flex items-center gap-2">
            {account && <ExchangeMark provider={account.provider} size={16} />}
            {account?.name ?? "Removed account"}
          </span>
        </div>
        <div className={detailRow}>
          <span className="text-[var(--text-muted)]">Strategy</span>
          {strategy && isLiveInstance(strategy) ? (
            <Link to={`/live/${strategy.id}`} className="text-[var(--primary)] no-underline" onClick={onClose}>
              {strategy.name} ›
            </Link>
          ) : (
            <span>{strategy ? strategy.name : o.backtest_id ? "Deleted strategy" : "Manual"}</span>
          )}
        </div>
        <div className={detailRow}>
          <span className="text-[var(--text-muted)]">Quantity</span>
          <span className="num">
            {num(qtyOf(o), 8)}
            {Number(o.requested_qty) > 0 && Number(o.requested_qty) !== qtyOf(o) && (
              <span className="ml-2 text-xs text-[var(--text-muted)]">requested {num(Number(o.requested_qty), 8)}</span>
            )}
          </span>
        </div>
        <div className={detailRow}>
          <span className="text-[var(--text-muted)]">Price</span>
          <span className="num">
            {num(price, 6)}
            {requested > 0 && requested !== price && <span className="ml-2 text-xs text-[var(--text-muted)]">requested {num(requested, 6)}</span>}
          </span>
        </div>
        <div className={detailRow}>
          <span className="text-[var(--text-muted)]">Value</span>
          <span className="num">{valueOf(o) ? usd(valueOf(o)) : "—"}</span>
        </div>
        <div className={detailRow}>
          <span className="text-[var(--text-muted)]">Order type</span>
          <span>{String(o.order_type || "market").toLowerCase()}</span>
        </div>
        <div className={detailRow}>
          <span className="text-[var(--text-muted)]">Exchange order ID</span>
          <span className="num select-all break-all text-right text-xs">{o.exchange_order_id || "—"}</span>
        </div>
        {o.exchange_status && (
          <div className={detailRow}>
            <span className="text-[var(--text-muted)]">Exchange status</span>
            <span className="text-xs">{o.exchange_status}</span>
          </div>
        )}
        {failed && o.error && (
          <div className={detailRow}>
            <span className="text-[var(--text-muted)]">Exchange said</span>
            <span className="break-all text-right text-xs text-[var(--text-secondary)]">{o.error}</span>
          </div>
        )}
      </div>
    </Dialog>
  );
}
